import React, { useContext } from 'react';
import BuilderContext from 'common/Context';
import Dropdown from 'common/Dropdown';
import { capitalize } from 'common/Utils';
import { Form } from 'react-final-form';
import { CheckboxField } from '@/components/common/Forms';
import I18n from 'modules/i18n';
import RadioButton from '@/components/common/Forms/RadioButton';
import { DraggableContainerPosition } from '../../types';
import styles from './SettingsButton.module.scss';

const ANSWER_CHOICES = 'answer_choices';
const DROP_ZONES = 'drop_zones';
const CHANGE_SHAPE = 'change_shape';
const CHANGE_POSITION = 'change_position';
const REUSABLE_ANSWER_CHOICES = 'reusable_answer_choices';
const POSITIONS = [
  DraggableContainerPosition.Left,
  DraggableContainerPosition.Right,
  DraggableContainerPosition.Top,
  DraggableContainerPosition.Bottom,
];

const SettingsButton = ({
  hasReusableAnswerChoices,
  hasTransparentAnswerChoices,
  hasTransparentDropzones,
}) => {
  const {
    dragAndDropSettingsDispatch,
    setDropZoneShape,
    dropZoneShape,
    dropZoneDispatch,
    draggableChoices,
    draggableContainerPosition,
    setDraggableContainerPosition,
    setPendingVocabSpacing,
    setShouldHideGrid,
    shouldHideGrid,
    vocabLinkOption,
  } = useContext(BuilderContext);
  const handleSettingsChange = (args, _state, _utils) => {
    const settingsType = args[0];

    if (settingsType === ANSWER_CHOICES) {
      dragAndDropSettingsDispatch({
        payload: { hasTransparentAnswerChoices: !hasTransparentAnswerChoices },
        type: 'SET_TRANSPARENT_ANSWER_CHOICES'
      });
    }
    else if (settingsType === DROP_ZONES) {
      dragAndDropSettingsDispatch({
        payload: { hasTransparentDropzones: !hasTransparentDropzones },
        type: 'SET_TRANSPARENT_DROPZONES'
      });
    }
    else if (settingsType === CHANGE_SHAPE) {
      dragAndDropSettingsDispatch({
        payload: { dropZoneShape: args[1] },
        type: 'SET_DROP_ZONE_SHAPE'
      });

      dropZoneDispatch({
        payload: { dropZoneShape: args[1] },
        type: 'UPDATE_DROP_ZONE_SHAPE'
      });

      // Only re-space the boxes if there are linked vocab/glossary terms; the layout effect in
      // Builder runs the measure/space/match pass once the shape change commits.
      if (draggableChoices.a?.vocabId) {
        setPendingVocabSpacing({ linkOption: vocabLinkOption, shape: args[1] });
      }

      setDropZoneShape(args[1]);
    }
    else if (settingsType === CHANGE_POSITION) {
      dragAndDropSettingsDispatch({
        payload: { draggableContainerPosition: args[1] },
        type: 'SET_DRAGGABLE_CONTAINER_POSITION'
      });
      setDraggableContainerPosition(args[1]);
    }
    else if (settingsType === REUSABLE_ANSWER_CHOICES) {
      dragAndDropSettingsDispatch({
        payload: { hasReusableAnswerChoices: !hasReusableAnswerChoices },
        type: 'SET_REUSABLE_ANSWER_CHOICES',
      });
    }
  };

  return (
    <div className="inline_block">
      <Dropdown
        buttonClass="btn btn--sm btn--green dropdown-toggle"
        dropdownClass="!tw-top-[45px] tw-w-[280px]"
        toggleText="Settings"
      >
        <Form
          initialValues={{ hasReusableAnswerChoices, hasTransparentAnswerChoices, hasTransparentDropzones }}
          mutators={{ handleSettingsChange: handleSettingsChange }}
          render={({ values, form }) => (
            <form
              className={styles.settingsForm}
              onSubmit={() => {}}
            >
              <CheckboxField
                checked={shouldHideGrid}
                fieldClass={styles.checkboxField}
                label="Hide Grid"
                labelClass={styles.checkboxLabel}
                name="shouldHideGrid"
                rowClass={styles.checkboxRow}
                onChange={() => setShouldHideGrid(!shouldHideGrid)}
              />

              <Dropdown
                buttonClass={`
                  !tw-cursor-pointer tw-bg-none tw-outline-none tw-border-none tw-bg-inherit tw-w-full tw-mt-1.5
                `}
                dropdownClass="tw-ml-5 tw-py-3 tw-pl-2 tw-pr-4"
                dropdownPosition="right"
                toggleText={(
                  <div className="!tw-cursor-pointer tw-flex tw-items-center tw-justify-between">
                    <div className={`!tw-cursor-pointer tw-ml-[25px] ${styles.labelStyle}`}>
                      Draggable Container Position
                    </div>
                    <i className="!tw-cursor-pointer fa fa-icon fa-caret-right" />
                  </div>
                )}
              >
                {POSITIONS.map(position => (
                  <RadioButton
                    key={position}
                    defaultChecked={draggableContainerPosition === position}
                    groupName="draggableContainerPosition"
                    label={capitalize(position)}
                    labelClassName="!tw-my-1"
                    value={position}
                    onChange={() => form.mutators.handleSettingsChange(CHANGE_POSITION, position)}
                  />
                ))}
              </Dropdown>

              <hr className="tw-border-light-grey" />

              <CheckboxField
                checked={values.hasTransparentAnswerChoices}
                fieldClass={styles.checkboxField}
                label={I18n.t('transparent_answer_choices')}
                labelClass={styles.checkboxLabel}
                name="hasTransparentAnswerChoices"
                rowClass={styles.checkboxRow}
                onChange={() => form.mutators.handleSettingsChange(ANSWER_CHOICES)}
              />

              <CheckboxField
                checked={values.hasReusableAnswerChoices}
                fieldClass={styles.checkboxField}
                label={I18n.t('reusable_answer_choices')}
                labelClass={styles.checkboxLabel}
                name="hasReusableAnswerChoices"
                rowClass={styles.checkboxRow}
                onChange={() => form.mutators.handleSettingsChange(REUSABLE_ANSWER_CHOICES)}
              />

              <hr className="tw-border-light-grey" />

              <CheckboxField
                checked={values.hasTransparentDropzones}
                fieldClass={styles.checkboxField}
                label={I18n.t('transparent_dropzones')}
                labelClass={styles.checkboxLabel}
                name="hasTransparentDropzones"
                rowClass={styles.checkboxRow}
                onChange={() => form.mutators.handleSettingsChange(DROP_ZONES)}
              />

              <Dropdown
                buttonClass={`
                  !tw-cursor-pointer tw-bg-none tw-outline-none tw-border-none tw-bg-inherit tw-w-full tw-mt-1.5
                `}
                dropdownClass="tw-ml-5 tw-py-3 tw-pl-2 tw-pr-4"
                dropdownPosition="right"
                toggleText={(
                  <div className="!tw-cursor-pointer tw-flex tw-items-center tw-justify-between">
                    <div className={`!tw-cursor-pointer tw-ml-[25px] ${styles.labelStyle}`}>Dropzone Shapes</div>
                    <i className="!tw-cursor-pointer fa fa-icon fa-caret-right" />
                  </div>
                )}
              >
                <RadioButton
                  defaultChecked={dropZoneShape === 'rectangle'}
                  groupName="dropzoneShape"
                  label={(
                    <div className="tw-flex tw-items-center">
                      <i className="fa fa-icon fa-square-o tw-text-xl tw-mx-1" />
                      <div className="tw-ml-1">Rectangles</div>
                    </div>
                  )}
                  labelClassName="tw-flex tw-items-center !tw-mt-0"
                  value="rectangle"
                  onChange={() => form.mutators.handleSettingsChange(CHANGE_SHAPE, 'rectangle')}
                />
                <RadioButton
                  defaultChecked={dropZoneShape === 'circle'}
                  groupName="dropzoneShape"
                  label={(
                    <div className="tw-flex tw-items-center">
                      <div className="fa fa-icon fa-circle-thin tw-text-xl tw-mx-1" />
                      <div className="tw-ml-1">Circles</div>
                    </div>
                  )}
                  labelClassName="tw-flex tw-items-center"
                  value="circle"
                  onChange={() => form.mutators.handleSettingsChange(CHANGE_SHAPE, 'circle')}
                />
              </Dropdown>
            </form>
          )}
          onSubmit={() => {}}
        />
      </Dropdown>
    </div>
  );
};

export default SettingsButton;
